import { UniqueIdentifier } from '@dnd-kit/core';

import { getPathById } from '@components/common/SortableTree';
import { formatMillisecondsAsMMSS } from '@components/ScreenerResults/utils';
import { TreeItem } from '@components/SurveyBuilder/types/models';
import { compact } from '@components/utils';

export const getStyle = (value, total) => ({
  backgroundColor: value ? `rgba(104, 117, 245, ${value / total})` : '#E5E7EB',
  color: value / total > 0.5 ? 'white' : '#374151'
});

type GetValue = (args: {
  showPercentage: boolean;
  value: number;
  totalAnswers: number;
  returnZeroValue?: boolean;
}) => string;

export const getValue: GetValue = ({ value, totalAnswers, showPercentage, returnZeroValue }) => {
  if (!value && !returnZeroValue) return '';

  if (!value && returnZeroValue) return showPercentage ? '0 %' : '0';

  const cell = showPercentage ? `${Math.round(((value || 0) / totalAnswers || 1) * 100)} %` : value.toString();

  return cell;
};

export const findNodeDeep = <T extends TreeItem>(items: T[], itemId: UniqueIdentifier): T | undefined => {
  for (const item of items) {
    const { id, nodes } = item;

    if (id === itemId) {
      return item;
    }

    if (nodes.length) {
      const node = findNodeDeep(nodes as TreeItem[], itemId) as T | undefined;

      if (node) {
        return node;
      }
    }
  }

  return undefined;
};

export const getSuccessCount = (
  answers: NonNullable<ScreenerResponseAnswerValue<'tree_test'>>[],
  path: UniqueIdentifier[]
) => {
  const count = answers.reduce((acc, answer) => {
    return answer.result.answer.join(',').startsWith(path.join(',')) ? acc + 1 : acc;
  }, 0);

  return count;
};

export const getDirectCount = (
  answers: NonNullable<ScreenerResponseAnswerValue<'tree_test'>>[],
  nodeId: string | number
) => {
  const count = answers.reduce((acc, answer) => {
    return answer.result.direct && answer.result.answer.includes(nodeId) ? acc + 1 : acc;
  }, 0);

  return count;
};

export const parseItems = (
  currentItems: TreeItem[],
  allItems: TreeItem[],
  answers: NonNullable<ScreenerResponseAnswerValue<'tree_test'>>[]
) => {
  return currentItems.map((item) => {
    const path = getPathById(allItems, item.id);
    const count = getSuccessCount(answers, path);
    const directCount = getDirectCount(answers, item.id);

    return {
      ...item,
      nodes: parseItems(item.nodes || [], allItems, answers),
      count,
      directCount,
      collapsed: !count
    };
  });
};

// this function is used to get the labels of the answers
// we used to store only node IDs as a result of the tree test
// but removing the node from the task broke the results
// that's why we store the labels of the answers as well
// but we need to support the old results as well (without labels)
// as it may affect the beta-testing users

export const getAnswerLabels = (
  result: NonNullable<ScreenerResponseAnswerValue<'tree_test'>>['result'],
  nodes: TreeItem[]
) => {
  if (result.answer_labels) {
    return result.answer_labels;
  } else {
    return compact(result.answer.map((answerId) => findNodeDeep(nodes, answerId)?.label));
  }
};

export const isCorrectNode = (answer: UniqueIdentifier, index: number, correctAnswers: UniqueIdentifier[][]) => {
  return correctAnswers.some((correctAnswer) => {
    const correctAnswerIndex = correctAnswer.findIndex((id) => id === answer);
    return correctAnswerIndex === index;
  });
};

export const getCsvData = (
  answers: NonNullable<ScreenerResponseAnswerValue<'tree_test'>>[],
  correctAnswers: UniqueIdentifier[][],
  nodes: TreeItem[] = []
) => {
  return answers.map((answer) => {
    const isCorrectAnswer = correctAnswers?.some(
      (correctAnswer) => correctAnswer.join(',') === answer.result.answer.join(',')
    );

    const answerLabels = getAnswerLabels(answer.result, nodes);

    const responseText = answerLabels.join(' > ');

    return {
      name: answer.participation_name || 'Unnamed',
      complete: answer.completed ? 'Yes' : 'No',
      successful: isCorrectAnswer ? 'Yes' : 'No',
      direct: answer.result.direct ? 'Yes' : 'No',
      total_time: answer.time_in_task ? formatMillisecondsAsMMSS(answer.time_in_task) : '00:00',
      response: responseText
    };
  });
};

export const getCsvDataForResultsTable = (
  nodes: TreeItem[],
  answers: NonNullable<ScreenerResponseAnswerValue<'tree_test'>>[],
  maxDepth: number,
  showPercentage: boolean = true
) => {
  // Parse items to get counts
  const parsedItems = parseItems(nodes, nodes, answers);

  // Build headers - first columns for tree depth, then Result and Direct
  const headers = [
    { label: 'Tree structure', key: 'depth_0' },
    ...Array.from({ length: maxDepth - 1 }, (_, i) => ({ label: '', key: `depth_${i + 1}` })),
    { label: 'Result', key: 'result' },
    { label: 'Direct', key: 'direct' }
  ];

  // Flatten tree into rows
  const data: Record<string, string>[] = [];

  const traverseTree = (items: TreeItem[], currentDepth: number = 0) => {
    items.forEach((item: any) => {
      const row: Record<string, string> = {};

      // Initialize all depth columns as empty
      for (let i = 0; i < maxDepth; i++) {
        row[`depth_${i}`] = '';
      }

      // Set the item label at the correct depth
      row[`depth_${currentDepth}`] = item.label || '';

      // Add Result and Direct values
      const resultValue = getValue({
        value: item.count || 0,
        totalAnswers: answers.length,
        showPercentage,
        returnZeroValue: true
      });
      const directValue = getValue({
        value: item.directCount || 0,
        totalAnswers: answers.length,
        showPercentage,
        returnZeroValue: true
      });

      row.result = resultValue;
      row.direct = directValue;

      data.push(row);

      // Recursively process child nodes
      if (item.nodes && item.nodes.length > 0) {
        traverseTree(item.nodes, currentDepth + 1);
      }
    });
  };

  traverseTree(parsedItems);

  return { headers, data };
};
